/**
 * IndexedDB snapshot of /api/notifications/preferences for the Service Worker.
 *
 * The SW (`packages/frontend/public/sw.js`) reads from key `notif-prefs` in
 * the `easyflow-prefs` database before calling `showNotification`, so it can
 * drop pushes that the user disabled or that fall inside quiet hours without
 * having to round-trip the backend on every push.
 *
 * Call sites:
 *  - AuthContext: on app boot (when a token is restored) and right after
 *    `login()` succeeds.
 *  - Settings page: after a successful PUT to /api/notifications/preferences
 *    (or any of the legacy endpoints) so the snapshot stays fresh without
 *    waiting for the next reload.
 *
 * Failure modes are non-fatal: if the API call fails, the snapshot is left
 * untouched and the SW falls back to "allow" — the backend has already gated
 * the push, so the worst case is one extra notification for one push cycle.
 */

import { apiClient as api } from '@/lib/api';

const DB_NAME = 'easyflow-prefs';
const DB_VERSION = 1;
const STORE = 'kv';
const KEY = 'notif-prefs';

interface PrefsSnapshot {
  categories: Record<string, { in_app: boolean; push: boolean; email: boolean; sound: boolean }>;
  quiet_hours: { enabled: boolean; start_time: string | null; end_time: string | null };
  /**
   * Tenant slug derived from the JWT, written so the Service Worker can
   * compose tenant-scoped URLs (e.g. /api/<tenantSlug>/notifications/:id/dismissed)
   * — the SW has no access to the JWT and cannot derive it on its own.
   */
  tenantSlug: string | null;
  updatedAt: number;
}

function readTenantSlugFromJwt(): string | null {
  try {
    const token = localStorage.getItem('edgvoip_tenant_token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]!));
    return payload.tenantSlug || payload.tenant_slug || null;
  } catch {
    return null;
  }
}

interface PreferencesPayload {
  categories?: Array<{
    category: string;
    in_app_enabled?: boolean;
    push_enabled?: boolean;
    email_enabled?: boolean;
    sound_enabled?: boolean;
  }>;
  quiet_hours?: { enabled?: boolean; start_time?: string | null; end_time?: string | null };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function writeSnapshot(snapshot: PrefsSnapshot): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(snapshot, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

function notifyServiceWorker(): void {
  try {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.getRegistration().then((reg) => {
      const sw = reg && (reg.active || reg.waiting || reg.installing);
      if (sw) sw.postMessage({ type: 'NOTIF_PREFS_UPDATED' });
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}

/**
 * Fetch the current preference matrix and persist it to IndexedDB so the
 * Service Worker can read it on the next push event. Resolves to `true` on
 * success, `false` on any failure (network error, IDB unavailable, malformed
 * payload). Never throws.
 */
export async function syncNotificationPrefsToSW(): Promise<boolean> {
  try {
    const res = await api.get<PreferencesPayload>('/notifications/preferences');
    const payload: PreferencesPayload | undefined = res?.data;
    if (!payload || !Array.isArray(payload.categories)) return false;

    const categories: PrefsSnapshot['categories'] = {};
    for (const row of payload.categories) {
      if (!row || typeof row.category !== 'string') continue;
      categories[row.category] = {
        in_app: row.in_app_enabled !== false,
        push: row.push_enabled !== false,
        email: row.email_enabled === true,
        sound: row.sound_enabled !== false,
      };
    }
    const qh = payload.quiet_hours || {};
    const snapshot: PrefsSnapshot = {
      categories,
      quiet_hours: {
        enabled: !!qh.enabled,
        start_time: qh.start_time || null,
        end_time: qh.end_time || null,
      },
      tenantSlug: readTenantSlugFromJwt(),
      updatedAt: Date.now(),
    };
    await writeSnapshot(snapshot);
    notifyServiceWorker();
    return true;
  } catch (err) {
    // Quiet failure: see file-level docstring for rationale.
    console.warn('[notif-prefs] snapshot sync failed:', (err as Error)?.message || err);
    return false;
  }
}

/**
 * Wipe the snapshot (e.g. on logout) so the SW doesn't keep a stale gating
 * matrix around for the previous user on shared devices.
 */
export async function clearNotificationPrefsSnapshot(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
    db.close();
    notifyServiceWorker();
  } catch {
    /* ignore */
  }
}
